/* eslint-disable @typescript-eslint/no-explicit-any */
import { Heading, useToast, VStack, Checkbox, Text, Link, useDisclosure, Spacer } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/atoms/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Input } from '../../../components/atoms/Input';
import { HiOutlineMailOpen } from 'react-icons/hi';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useMutation } from 'react-query';
import { Footer } from '../../../components/organisms/Footer';
import { ShowPasswordToggle } from '@/components/atoms/ShowPasswordToggle';
import { useProfile } from '@/contexts/ProfileProvider';
import { vinculateExternalCode, validatePromocode, vinculateCPF, registerUser } from '@/service/auth/tokens';
import { isAuthenticated, setAccessToken } from '@/service/axios';
import { useAPICallAsMutation } from '@/service/hooks';
import { masks } from '@/utils/masks';
import { validarCPF } from '@/utils/validateDocuments';
import { ModalCPF } from '@/components/organisms/ModalCPF';
import { ICodeCheckResponse, IPlan } from '@/service/types';

interface IUserRegistrationData {
  full_name: string;
  email: string;
  telephone: string;
  cpf: string;
  password: string;
  promocode?: string;
  accessCode?: string;
  external_code?: string;
}

interface VinculatePlan {
  id: number;
  plan: IPlan;
  plan_children: IPlan[];
}

export function RegisterForm() {
  const toast = useToast();
  const navigate = useNavigate();
  const { id, colors, cpfModule, allowPromocodeLoginMethod, metadata } = useTheme();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registerType, setRegisterType] = useState<'promocode' | 'cpf' | 'subscription' | 'externalCode' | null>(null);

  const { updateProfile } = useProfile();
  const [cnpjValue, setCnpjValue] = useState<string | null>(null);
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [activatedPlan, setActivatedPlan] = useState<VinculatePlan | null>(null);

  const modalCpf = useDisclosure();

  if (isAuthenticated()) navigate('/');

  const passwordSchema = z
    .string()
    .min(8, { message: 'A senha deve ter pelo menos 8 caracteres.' })
    .regex(/[A-Z]/, { message: 'A senha deve conter pelo menos uma letra maiúscula.' })
    .regex(/[a-z]/, { message: 'A senha deve conter pelo menos uma letra minúscula.' })
    .regex(/[0-9]/, { message: 'A senha deve conter pelo menos um número.' })
    .regex(/[^a-zA-Z0-9]/, { message: 'A senha deve conter pelo menos um caractere especial.' });

  const formSchema = z.object({
    full_name: z.string().min(1),
    email: z.string().email(),
    cpf: z.string().refine((value) => {
      return validarCPF(value);
    }),
    password: passwordSchema,
  });

  const methods = useForm<IUserRegistrationData>({
    resolver: zodResolver(formSchema),
  });

  const { control, watch, handleSubmit, setValue, reset } = methods;

  const accessCode = watch('accessCode');

  const accessCodeLabel = useMemo(() => {
    if (registerType === 'promocode') return 'Código Promocional';
    else if (registerType === 'cpf') return 'CPF / CNPJ';

    return 'Código de Acesso';
  }, [registerType]);

  const validatorFunc = useCallback(async () => {
    if (!id) return false;
    if (!accessCode) return false;

    if (registerType === 'externalCode') {
      return vinculateExternalCode(accessCode, id);
    }

    if (registerType === 'promocode') return validatePromocode(accessCode);
    else if (registerType === 'cpf') {
      return vinculateCPF(accessCode, id);
    }
  }, [registerType, id, accessCode]);

  const validatorMutation = useMutation(() => validatorFunc() as Promise<ICodeCheckResponse>, {
    onSuccess(data: any) {
      reset();
      if (data?.first_signup && data?.is_cnpj) {
        modalCpf.onOpen();
        setCnpjValue(accessCode || '');
      }
      if (data?.is_cnpj && data?.document_for_cnpj) {
        setValue('cpf', masks.cpf(data?.document_for_cnpj));
      }

      if (registerType === 'cpf' && accessCode) {
        setValue('cpf', accessCode);
      }

      if (data.plan) {
        setActivatedPlan(data);
      }

      if (accessCode) {
        toast({
          title: 'Sucesso!',
          description: `Código de Acesso ${accessCode} validado com sucesso!, realize o cadastro.`,
          status: 'success',
        });
        setAccessCodeValidated(true);
      }
    },
    onError(err: any) {
      let errorMessage = `Parece que este ${accessCodeLabel} não é valido. Por favor verifique se está correto.`;
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      toast({
        title: 'Oops!',
        description: errorMessage,
        status: 'error',
      });
    },
  });

  console.log(watch('cpf'));
  const registerMutation = useAPICallAsMutation(
    (data: IUserRegistrationData) => {
      if (registerType === 'promocode') {
        data.promocode = accessCode || undefined;
      }
      delete data.accessCode;
      if (registerType === 'externalCode') {
        data.external_code = accessCode ? masks.unmask(accessCode) : undefined;
      }
      return registerUser(data, id || 0);
    },
    {
      onSuccess(data) {
        setAccessToken(data.token);
        updateProfile({});
        navigate('/');
      },
      onError(error: any) {
        let message: any = 'Erro ao criar conta. Revise as informações do formulário e tente novamente';
        if (error.response.data.cpf && error.response.data.email) {
          message = 'CPF e Email já cadastrados. Por favor, faça login ou recupere sua senha.';
        } else if (error.response.data.cpf) {
          message = 'CPF já cadastrado. Por favor, faça login ou recupere sua senha.';
        } else if (error.response.data.email) {
          message = 'Email já cadastrado. Por favor, faça login ou recupere sua senha.';
        } else if (error.response.data.promocode) {
          message = error.response.data.promocode;
        }

        toast({
          title: 'Oops!',
          description: message,
          status: 'error',
        });
      },
    }
  );

  if (!registerType)
    return (
      <VStack align={'flex-start'} width={'100%'} spacing={5}>
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'28px'}>
          Registrar com:
        </Heading>
        {cpfModule && (
          <Button onClick={() => setRegisterType('cpf')}>{metadata?.buttoncpfname || 'CPF/CNPJ Vinculado'}</Button>
        )}
        {allowPromocodeLoginMethod && <Button onClick={() => setRegisterType('promocode')}>Código Promocional</Button>}
        {metadata?.subscription !== false && (
          <Button onClick={() => setRegisterType('subscription')}>Assinatura</Button>
        )}
        {metadata?.moduleexternalcode && (
          <Button onClick={() => setRegisterType('externalCode')}>
            {metadata?.buttonsubscriptionexternalcode || 'Assinatura com código externo'}
          </Button>
        )}

        <Text
          color="#747474"
          margin="auto"
          textAlign={'center'}
          alignItems={'center'}
          gap={1}
          fontSize={14}
          fontWeight={400}
        >
          Ao continuar você está de acordo com nossos{'  '}
          <span
            onClick={() => navigate('/legal/termos-de-uso')}
            style={{ cursor: 'pointer', color: colors.primaryColor }}
          >
            Termos de Uso
          </span>
          {'  '}e{'  '}
          <span
            onClick={() => navigate('/legal/politica-de-privacidade')}
            style={{ cursor: 'pointer', color: colors.primaryColor }}
          >
            Política de Privacidade
          </span>
        </Text>
      </VStack>
    );
  if (!accessCodeValidated && registerType !== 'subscription')
    return (
      <VStack align={'flex-start'} spacing={5} width={'100%'}>
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'28px'}>
          {accessCodeLabel}
        </Heading>
        {registerType === 'promocode' ? (
          <Input label="Código" control={control} name="accessCode" autoComplete="off" />
        ) : registerType === 'externalCode' ? (
          <Input
            control={control}
            name="accessCode"
            mask={metadata?.masksubscriptionexternalcode === 'phone' ? 'cell' : undefined}
            label={metadata.buttonsubscriptionexternalcode}
            autoComplete="off"
          />
        ) : (
          <Input control={control} name="accessCode" label="CPF/CNPJ" mask="document" autoComplete="off" />
        )}

        <Button onClick={() => validatorMutation.mutate()} isLoading={validatorMutation.isLoading}>
          Verificar
        </Button>
        <Link
          onClick={() => {
            setValue('accessCode', '');
            setRegisterType(null);
            setAccessCodeValidated(false);
          }}
          margin={'auto'}
          color={colors.primaryColor}
        >
          Voltar
        </Link>
      </VStack>
    );

  return (
    <VStack w={'100%'} align={'flex-start'} spacing={4}>
      {activatedPlan ? (
        <VStack
          padding={'24px'}
          align={'flex-start'}
          width={'100%'}
          borderRadius={'16px'}
          backgroundColor="primaryColor"
          spacing={4}
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
        >
          <Heading color="white" fontSize={'24px'} fontWeight={600} letterSpacing="-0.02em">
            🎉 Parabéns!
          </Heading>

          <VStack align="flex-start" spacing={3} width="100%">
            <Text color="white" fontSize={'16px'} fontWeight={500} opacity={0.9}>
              Seu {registerType === 'cpf' ? 'CPF' : registerType === 'promocode' ? 'Promocode' : 'Código'} garante
              acesso a:
            </Text>

            <VStack align="flex-start" spacing={2} pl={4} width="100%">
              <Text color="white" fontSize={'15px'} fontWeight={600} display="flex" alignItems="center" gap={2}>
                ✓ {activatedPlan?.plan?.display_name}
              </Text>

              {activatedPlan?.plan_children?.map((child, index) => (
                <Text
                  key={index}
                  color="white"
                  fontSize={'15px'}
                  fontWeight={600}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  ✓ {child.display_name}
                </Text>
              ))}
            </VStack>
          </VStack>
        </VStack>
      ) : (
        <Heading fontWeight={700} letterSpacing={'-0.045em'} fontFamily={'Glory'} fontSize={'36px'}>
          Cadastre-se
        </Heading>
      )}
      <form
        style={{ width: '100%' }}
        onSubmit={handleSubmit((data) => registerMutation.mutate(data as IUserRegistrationData))}
      >
        <VStack spacing={7} w={'100%'} align={'center'}>
          <Input control={control} name="full_name" label="Nome" autoComplete="off" />
          <Input control={control} name="cpf" label="CPF" mask="cpf" autoComplete="off" />
          <Input
            control={control}
            autoComplete="off"
            name="email"
            label="Email"
            rightElement={<HiOutlineMailOpen color="#292927" />}
          />
          <Input
            control={control}
            name="password"
            label="Criar Senha"
            autoComplete="new-password"
            type={showPassword ? 'text' : 'password'}
            rightElement={<ShowPasswordToggle value={showPassword} onChange={setShowPassword} />}
          />
          <Checkbox color="#292927" isChecked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}>
            Concordo com os{'  '}
            <span
              onClick={() => navigate('/legal/termos-de-uso')}
              style={{ cursor: 'pointer', color: colors.primaryColor }}
            >
              Termos de Uso
            </span>
            {'  '}e{'  '}
            <span
              onClick={() => navigate('/legal/politica-de-privacidade')}
              style={{ cursor: 'pointer', color: colors.primaryColor }}
            >
              Política de Privacidade
            </span>
          </Checkbox>

          <Button isDisabled={!acceptedTerms} isLoading={registerMutation.isLoading} type="submit">
            CRIAR CONTA
          </Button>

          <Link
            onClick={() => {
              setValue('accessCode', '');
              setRegisterType(null);
              setAccessCodeValidated(false);
            }}
            color={colors.primaryColor}
          >
            Voltar
          </Link>

          <Footer />
          <Spacer
            mb={{
              base: '50px',
              md: '0px',
            }}
          />
        </VStack>
      </form>
      <ModalCPF
        document={cnpjValue || undefined}
        isOpen={modalCpf.isOpen}
        onClose={(cpf) => {
          setValue('cpf', cpf);
          modalCpf.onClose();
        }}
      />
    </VStack>
  );
}
